import { Trans, useTranslation } from "react-i18next";
import JuisciLogo from "../../assets/core/juisci-logo.svg?react";

/**
 * Brand panel shared by every desktop auth screen (signup, login, password
 * reset, magic link, onboarding). Cream surface (the logo mark is black-fill),
 * wordmark on top, product promise at the bottom. Hidden below the desktop
 * breakpoint — mobile renders its own full-screen layouts.
 */
export function AuthBrandPanel(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <aside className="hidden w-2/5 max-w-xl shrink-0 flex-col justify-between bg-ds-cream p-10 md:flex lg:p-14">
      <div className="[&_svg]:h-9 [&_svg]:w-auto self-start">
        <JuisciLogo />
      </div>
      <div className="flex flex-col gap-4">
        <h1 className="font-dm-sans text-4xl font-extrabold tracking-[-0.03em] text-ds-black">
          <Trans
            i18nKey="The clinical research agent,<br /><serif>built for doctors.</serif>"
            components={{
              br: <br />,
              serif: <span className="font-petrona italic" />,
            }}
          />
        </h1>
        <p className="font-dm-sans text-heading-xs text-ds-slate-600">
          {t("Every answer grounded in peer-reviewed evidence.")}
        </p>
      </div>
    </aside>
  );
}

/**
 * Desktop split-screen shell for the standalone auth pages (signup, login,
 * password reset, magic link, email confirmation): brand panel on the left,
 * vertically-centered content column on the right. Rendered only on desktop
 * web (the pages dispatch on `isDesktopLayout()`), so the brand panel's `md:flex`
 * is always active here — it exists so the panel also degrades gracefully if
 * the viewport shrinks below the breakpoint after mount.
 */
export default function DesktopAuthLayout({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex h-dvh w-full bg-ds-white">
      <AuthBrandPanel />
      <main className="flex min-w-0 flex-1 flex-col overflow-y-auto">
        <div className="m-auto  flex w-full max-w-md flex-col px-6 py-12">
          {children}
        </div>
      </main>
    </div>
  );
}
